<script lang="ts" setup>
import { PlayIcon, PauseIcon } from 'tir-style-system/icons/solid'
import {
  PencilSquareIcon,
  ArrowDownTrayIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon,
} from 'tir-style-system/icons/solid'
import { PopoverPositionEnum, TirPmPopover } from 'tir-pm-popover'
import { TirPmInput } from 'tir-pm-input'
import {
  TirPmButton,
  TirPmButtonSizeEnum,
  TirPmButtonVariantEnum,
} from 'tir-pm-button'
import { computed, onBeforeMount, ref, watch } from 'vue'

/**Параметры */
const props = defineProps({
  /**Текущее время просмотра */
  currentTime: { type: Number },
  /**Время видео */
  duration: { type: Number },
  /**Проигрывается ли видео */
  play: { type: Boolean },
  /**Активен полноэкранный режим */
  fullscreen: { type: Boolean },
  /**Имя видеозаписи */
  name: { type: String },
})
/**События */
const emit = defineEmits<{
  /**Переключение проигрывания */
  'update:play': [isPlay: boolean]
  /**Переключение полноэкранного режима */
  'update:fullscreen': [isFullscreen: boolean]
  /**Редактировать имя видео */
  edit: []
  /**Скачать видео */
  download: []
  /**Отслеживание переключений поповера */
  popover: [isOpen: boolean]
  /**Обновление имени поповера */
  updateName: [name: string]
}>()
const nameWithoutExtension = computed(() => props.name && removeFileExtension(props.name))
/**Локальное имя файла */
const innerName = ref('')
/**Поповер с формой редактирования имени */
const $namePopover = ref<InstanceType<typeof TirPmPopover>>()

/**Время проигрывания */
const time = computed(() => {
  const currentTime = Number((props.currentTime || 0).toFixed(0))
  const duration = Number((props.duration || 0).toFixed(0))

  return `${getParsedTime(currentTime)} / ${getParsedTime(duration)}`
})

/**Отслеживание изменений имени файла */
watch(() => props.name, setName)

/**Перед рендером */
onBeforeMount(() => setName())

const removeFileExtension = (fileName: string): string => {
  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex === -1) return fileName; // Если точки нет, возвращаем исходное имя
  return fileName.substring(0, lastDotIndex);
};

/**Установка значения поля innerName */
function setName() {
  innerName.value = removeFileExtension(props.name || '');
}
/**Получить время в формате 01:01:01 */
const getParsedTime = (d: number) => {
  const hours = upScaleLength(Math.floor(d / 3600))
  const minutes = upScaleLength(Math.floor((d % 3600) / 60))
  const seconds = upScaleLength(Math.floor((d % 3600) % 60))
  return `${hours}:${minutes}:${seconds}`
}

/**Переключение пригрывания */
const togglePlay = () => {
  emit('update:play', !props.play)
}
/**Переключение полноэкранного режима */
const toggleFullscreen = () => {
  emit('update:fullscreen', !props.fullscreen)
}
/**Скачать видео */
const download = () => {
  emit('download')
}
/**Добавление "0" для таймера */
const upScaleLength = (_seconds: number) => {
  const seconds = _seconds.toString()
  return seconds.length < 2 ? '0' + seconds : seconds
}
/**Отслеживание видимости поповера */
const togglePopover = (isOpen: boolean) => {
  emit('popover', isOpen)
}
/**Закрыть поповер с редактированием имени */
const closePopover = () => {
  $namePopover.value?.close()
}
/**Сохранение имени поповера */
const saveName = () => {
  emit('updateName', innerName.value)
  closePopover()
}
</script>
<template>
  <div class="tir-pm-video-player-controls">
    <div v-text="time" class="tir-pm-video-player-controls_time" />
    <div class="tir-pm-video-player-controls_button middle" @click="togglePlay">
      <PauseIcon v-if="play" />
      <PlayIcon v-else />
    </div>
    <TirPmPopover
      ref="$namePopover"
      width="25rem"
      :position="PopoverPositionEnum.Top"
      @open="togglePopover(true)"
      @close="togglePopover(false)"
    >
      <template #default>
        <div class="tir-pm-video-player-controls_button" @click="emit('edit')">
          <PencilSquareIcon />
        </div>
      </template>
      <template #content>
        <div class="tir-pm-video-player-controls_edit">
          <TirPmInput
            v-model="innerName"
            is-small-size
            auto-focus
            label="Название файла"
            :is-with-hint="false"
            @enter="saveName"
          />
          <div class="tir-pm-video-player-controls_edit_buttons">
            <TirPmButton
              :variant="TirPmButtonVariantEnum.PrimaryState"
              :size="TirPmButtonSizeEnum.Small"
              :is-disabled="nameWithoutExtension == innerName"
              @click="saveName"
            >
              Сохранить
            </TirPmButton>
            <TirPmButton
              :variant="TirPmButtonVariantEnum.SecondaryState"
              :size="TirPmButtonSizeEnum.Small"
              @click="closePopover"
            >
              Отмена
            </TirPmButton>
          </div>
        </div>
      </template>
    </TirPmPopover>
    <div class="tir-pm-video-player-controls_button" @click="download">
      <ArrowDownTrayIcon />
    </div>
    <div class="tir-pm-video-player-controls_button" @click="toggleFullscreen">
      <ArrowsPointingInIcon v-if="fullscreen" />
      <ArrowsPointingOutIcon v-else />
    </div>
  </div>
</template>
<style lang="scss" scoped>
.tir-pm-video-player-controls {
  position: relative;
  display: flex;
  align-items: center;
  background-color: var(--neutral-100, #fff);
  color: var(--neutral-0, #272d37);
  width: 100%;
  height: 2rem;
  padding: 0 1.5rem;
  user-select: none;
  overflow: hidden;
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  overflow: hidden;
  z-index: 1;
  &_time {
    color: var(--neutral-0, #272d37);
    font-size: 0.75rem;
    font-weight: 400;
    margin-right: auto;
  }
  &_button {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 2rem;
    height: 2rem;
    cursor: pointer;
    transition: 0.2s linear;

    &:hover {
      background-color: var(--neutral-translucent-5);
    }
    & > svg {
      width: 1rem;
      height: 1rem;
    }
    &.middle {
      position: absolute;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
    }
  }
  &_edit {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.75rem;
    border-radius: 0.5rem;
    background: var(--bg-base);
    &_buttons {
      display: flex;
      gap: 0.5rem;
    }
  }
}
</style>
